/**
 * DeepSeaModal
 *
 * 深海領域のカード一覧を表示するモーダル
 * - カードの深度・ボーナス表示
 * - 浮上ボタン
 * - PC: 中央表示、モバイル: 下部スライドアップ
 */

import React from 'react';
import Card from '../../components/Card';
import type { DeepSeaZone, DeepSeaCard } from '../../engine/deepSeaZone/types';
import type { PlayerNumber } from '../../types/player';
import { getDepthBonus, calculateSurfaceCost } from '../../engine/deepSeaZone/constants';
import { useBattle } from '../context';

interface DeepSeaModalProps {
  /** 表示中かどうか */
  isOpen: boolean;
  /** 閉じるコールバック */
  onClose: () => void;
  /** 深海領域の状態 */
  deepSeaZone: DeepSeaZone;
  /** プレイヤー番号 */
  player: PlayerNumber;
  /** 使用可能なSP */
  activeSP: number;
  /** フィールドの空きスロット数 */
  emptyFieldSlots: number;
  /** コスト軽減量 */
  costReduction?: number;
  /** 浮上コールバック */
  onSurface: (deepSeaIndex: number, fieldSlot: number) => void;
  /** モバイル表示かどうか */
  isMobile?: boolean;
}

const DeepSeaModal: React.FC<DeepSeaModalProps> = ({
  isOpen,
  onClose,
  deepSeaZone,
  player,
  activeSP,
  emptyFieldSlots,
  costReduction = 0,
  onSurface,
  isMobile = false,
}) => {
  // カード詳細表示用
  const { setMobileCardDetail } = useBattle();

  if (!isOpen) return null;

  const { cards, maxCapacity } = deepSeaZone;

  // 浮上可能判定
  const canSurfaceCard = (deepSeaCard: DeepSeaCard): boolean => {
    if (emptyFieldSlots <= 0) return false;
    const cost = calculateSurfaceCost(deepSeaCard.card.cost || 0, deepSeaCard.depth, costReduction);
    return activeSP >= cost;
  };

  // 浮上コスト取得
  const getSurfaceCostForCard = (deepSeaCard: DeepSeaCard): number => {
    return calculateSurfaceCost(deepSeaCard.card.cost || 0, deepSeaCard.depth, costReduction);
  };

  // 浮上処理
  const handleSurface = (index: number) => {
    // 空きスロットの最初を使用（フィールドスロットの選択は呼び出し元に任せる）
    onSurface(index, -1); // -1は自動選択を示す
    onClose();
  };

  // 深度ボーナス説明テキスト
  const getDepthBonusText = (depth: number): string => {
    const bonus = getDepthBonus(depth);
    const parts: string[] = [];
    if (bonus.attackBonus > 0) parts.push(`ATK+${bonus.attackBonus}`);
    if (bonus.canPreemptiveStrike) parts.push('先制攻撃');
    if (bonus.triggerSurfaceEffect) parts.push('浮上時効果');
    return parts.length > 0 ? parts.join(', ') : '—';
  };

  // スタイル定義
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: isMobile ? 'flex-end' : 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const modalStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #0a1628, #0d2137)',
    borderRadius: isMobile ? '16px 16px 0 0' : '12px',
    padding: isMobile ? '16px' : '20px',
    width: isMobile ? '100%' : 'auto',
    maxWidth: isMobile ? '100%' : '600px',
    maxHeight: isMobile ? '80vh' : '70vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    border: '2px solid rgba(0, 150, 255, 0.4)',
    boxShadow: '0 0 30px rgba(0, 100, 200, 0.5)',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid rgba(0, 150, 255, 0.3)',
  };

  const titleStyle: React.CSSProperties = {
    margin: 0,
    color: '#4dd0e1',
    fontSize: isMobile ? '16px' : '18px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'rgba(255, 68, 68, 0.8)',
    border: 'none',
    borderRadius: '6px',
    color: 'white',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
  };

  const infoBarStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    background: 'rgba(0, 80, 120, 0.3)',
    borderRadius: '8px',
    marginBottom: '12px',
    fontSize: isMobile ? '12px' : '13px',
  };

  const cardGridStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: isMobile ? '12px' : '16px',
    justifyContent: 'center',
    overflowY: 'auto',
    padding: '8px',
    flex: 1,
  };

  const cardContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    background: 'rgba(0, 60, 100, 0.3)',
    borderRadius: '8px',
    border: '1px solid rgba(0, 150, 255, 0.3)',
  };

  const depthBadgeStyle = (depth: number): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    background: depth >= 5 ? 'linear-gradient(135deg, #ff6b00, #ff9800)' :
                depth >= 3 ? 'linear-gradient(135deg, #4caf50, #66bb6a)' :
                'linear-gradient(135deg, #2196f3, #42a5f5)',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#fff',
  });

  const bonusTextStyle: React.CSSProperties = {
    fontSize: '11px',
    color: '#8ab4d4',
    textAlign: 'center',
    maxWidth: '120px',
  };

  const surfaceButtonStyle = (canSurface: boolean): React.CSSProperties => ({
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: canSurface ? 'pointer' : 'not-allowed',
    fontWeight: 'bold',
    fontSize: '12px',
    background: canSurface
      ? 'linear-gradient(135deg, #00bcd4, #4dd0e1)'
      : 'linear-gradient(135deg, #444, #666)',
    color: '#fff',
    opacity: canSurface ? 1 : 0.5,
    transition: 'all 0.2s ease',
  });

  const emptyMessageStyle: React.CSSProperties = {
    color: '#8ab4d4',
    textAlign: 'center',
    padding: '40px 20px',
    fontSize: '14px',
  };

  const legendStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    justifyContent: 'center',
    padding: '12px',
    background: 'rgba(0, 60, 100, 0.2)',
    borderRadius: '8px',
    marginTop: '12px',
    fontSize: '11px',
    color: '#8ab4d4',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* ヘッダー */}
        <div style={headerStyle}>
          <h3 style={titleStyle}>
            <span>🌊</span>
            深海領域
            <span style={{ fontSize: '14px', color: '#8ab4d4' }}>
              ({cards.length}/{maxCapacity})
            </span>
          </h3>
          <button style={closeButtonStyle} onClick={onClose}>
            ✕ 閉じる
          </button>
        </div>

        {/* 情報バー */}
        <div style={infoBarStyle}>
          <span style={{ color: '#4dd0e1' }}>
            💠 SP: <strong>{activeSP}</strong>
          </span>
          <span style={{ color: emptyFieldSlots > 0 ? '#66bb6a' : '#ff6b6b' }}>
            🏟️ 空きスロット: <strong>{emptyFieldSlots}</strong>
          </span>
          {costReduction > 0 && (
            <span style={{ color: '#ffc107' }}>
              ⬇️ コスト軽減: <strong>-{costReduction}</strong>
            </span>
          )}
        </div>

        {/* カード一覧 */}
        <div style={cardGridStyle}>
          {cards.length === 0 ? (
            <div style={emptyMessageStyle}>
              深海領域にカードがありません。<br />
              【潜航】を持つモンスターを手札から潜航させましょう。
            </div>
          ) : (
            cards.map((deepSeaCard, index) => {
              const canSurface = canSurfaceCard(deepSeaCard);
              const surfaceCost = getSurfaceCostForCard(deepSeaCard);
              const bonus = getDepthBonus(deepSeaCard.depth);

              // カード詳細を表示
              const showCardDetail = () => {
                setMobileCardDetail({ card: deepSeaCard.card, source: 'deepSea' });
              };

              return (
                <div key={deepSeaCard.uniqueId || index} style={cardContainerStyle}>
                  <div
                    onClick={showCardDetail}
                    style={{ cursor: 'pointer' }}
                  >
                    <Card card={deepSeaCard.card} small />
                  </div>

                  {/* 深度バッジ */}
                  <div style={depthBadgeStyle(deepSeaCard.depth)}>
                    <span>🔱</span>
                    <span>深度 {deepSeaCard.depth}</span>
                  </div>

                  {/* ボーナス表示 */}
                  <div style={bonusTextStyle}>
                    {getDepthBonusText(deepSeaCard.depth)}
                  </div>

                  {/* ボタン群 */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    {/* 詳細ボタン */}
                    <button
                      onClick={showCardDetail}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '11px',
                        background: 'linear-gradient(135deg, #2196f3, #42a5f5)',
                        color: '#fff',
                      }}
                    >
                      🔍 詳細
                    </button>

                    {/* 浮上ボタン */}
                    <button
                      style={surfaceButtonStyle(canSurface)}
                      onClick={() => canSurface && handleSurface(index)}
                      disabled={!canSurface}
                      title={canSurface ? `SP${surfaceCost}で浮上` : 'SP不足または空きスロットなし'}
                    >
                      🌅 浮上 (SP{surfaceCost})
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* 凡例 */}
        <div style={legendStyle}>
          <span>📊 深度ボーナス:</span>
          <span style={{ color: '#42a5f5' }}>深度2+ ATK+200</span>
          <span style={{ color: '#66bb6a' }}>深度3+ 先制攻撃</span>
          <span style={{ color: '#ff9800' }}>深度5+ 浮上時効果</span>
        </div>
      </div>
    </div>
  );
};

export default DeepSeaModal;
